import { api } from "./api";

/**
 * Tray Master — /tray/* on the CPOS backend.
 *
 * The shared `api` instance's baseURL already ends in /api, so paths start at
 * /tray. Every call needs the 'tray' permission module (list: read, create:
 * create, update/active: update, inactive: delete).
 *
 * The API rejects, with a message worth surfacing verbatim:
 *   404  Tray <id> not found
 *   409  Tray <barcode> is reserved for drop list|pick list <no>; release it before inactivating
 *   403  You do not have permission to perform this action
 */

function buildParams(obj) {
  const out = {};
  Object.keys(obj || {}).forEach((key) => {
    const value = obj[key];
    if (value === null || value === undefined || value === "") return;
    out[key] = value;
  });
  return out;
}

/**
 * GET /tray/list — paged, newest first; `searchValue` matches name and barcode,
 * `status` is "active" | "inactive". Response data is { totalCount, trays }.
 */
export const trayListApi = ({ pageNumber = 1, pageSize = 20, searchValue, status } = {}) =>
  api.get("/tray/list", {
    params: buildParams({ pageNumber, pageSize, searchValue, status }),
  });

/**
 * GET /tray/holder?tray_id= — the tray with the document holding it. Response
 * data is { tray_id, tray_name, barcode, is_active, is_locked,
 * locked_type: "droplist" | "picklist" | null, locked_id, locked_no } (locked_*
 * null when the tray is free). 404 when the tray doesn't exist.
 */
export const trayHolderApi = (tray_id) => api.get("/tray/holder", { params: { tray_id } });

/** POST /tray/create { tray_name } — the barcode is generated by the server. */
export const trayCreateApi = (params) => api.post("/tray/create", JSON.stringify(params));

/**
 * POST /tray/bulk-create { count } — generates `count` active trays (1–500), each
 * named "Tray <barcode>". Response data is { count, trays } sorted by barcode.
 */
export const trayBulkCreateApi = (count) =>
  api.post("/tray/bulk-create", JSON.stringify({ count }));

/** POST /tray/update { tray_id, tray_name } — renames a tray. */
export const trayUpdateApi = (params) => api.post("/tray/update", JSON.stringify(params));

/** POST /tray/inactive { tray_id } — refused while a drop list or pick list holds the tray. */
export const trayInactiveApi = (tray_id) =>
  api.post("/tray/inactive", JSON.stringify({ tray_id }));

/** POST /tray/active { tray_id } — makes an inactive tray reservable again. */
export const trayActiveApi = (tray_id) => api.post("/tray/active", JSON.stringify({ tray_id }));

/**
 * POST /reserve/tray?type=droplist|picklist { id, barcode } — locks the tray to
 * that document (`id` is its drop_list_id / pick_list_id). Response data is the tray.
 * 409 when the tray is inactive or another document of the type holds it.
 */
export const trayReserveApi = (type, id, barcode) =>
  api.post("/reserve/tray", JSON.stringify({ id, barcode }), { params: { type } });

/**
 * POST /release/tray?type=droplist|picklist { id, barcode } — frees a tray the
 * document holds. Response data is { tray_id, tray_name, barcode }.
 */
export const trayReleaseApi = (type, id, barcode) =>
  api.post("/release/tray", JSON.stringify({ id, barcode }), { params: { type } });
